import type { Stop } from "../types";
import { API_ENDPOINTS } from "./apiEndpoints";
import { getFeatureTypeName, getActiveTpid } from "./timetablePeriods";

// Fetch traffic meta (iconCss, groupCss) for a line via the "find" endpoint
export async function fetchTrafficMetaViaFind(
  lineId: string
): Promise<{ iconCss?: string; groupCss?: string }> {
  const lid = String(lineId).trim();
  if (!lid) return {};

  const detRes = await fetch(API_ENDPOINTS.LINES_DETAILS(lid));

  if (!detRes.ok) {
    console.error(
      "[trafficMetaViaFind] details error:",
      lid,
      detRes.status,
      await detRes.text().catch(() => "")
    );
    return {};
  }

  const detJson = await detRes.json();
  const details = Array.isArray(detJson) ? detJson[0] : detJson;

  let periodId = await getActiveTpid();

  if (periodId == null) {
    periodId = pickActivePeriodId(details);
  }

  if (!periodId) {
    console.warn("[trafficMetaViaFind] no active period for line", lid, {
      details,
    });
    return pickTrafficMeta(details);
  }

  const queryCandidates = uniqueStrings([
    details?.LineName,
    details?.LineNrReal,
    details?.PublicLineNumber,
    details?.LineNo,
    details?.LineNumber,
    details?.Designation,
    details?.Name,
    details?.name,
    lid,
  ]);

  let lastFindArray: any[] = [];

  for (const query of queryCandidates) {
    const findUrl = API_ENDPOINTS.LINES_FIND(query, String(periodId), true);

    const findRes = await fetch(findUrl);

    if (!findRes.ok) {
      console.error(
        "[trafficMetaViaFind] find error:",
        { lid, query, periodId },
        findRes.status,
        await findRes.text().catch(() => "")
      );
      continue;
    }

    const findJson = await findRes.json();
    const findArr = Array.isArray(findJson) ? findJson : [];

    lastFindArray = findArr;

    const rec = findMatchingTrafficRecord(findArr, details, lid, query);

    const fromRec = pickTrafficMeta(rec);

    if (fromRec.iconCss || fromRec.groupCss) {
      return fromRec;
    }

    // Om sökningen ger exakt en träff, använd den även om matchningen ovan missade.
    if (findArr.length === 1) {
      const only = pickTrafficMeta(findArr[0]);

      if (only.iconCss || only.groupCss) {
        return only;
      }
    }
  }

  const fromDetails = pickTrafficMeta(details);

  if (fromDetails.iconCss || fromDetails.groupCss) {
    return fromDetails;
  }

  console.warn("[trafficMetaViaFind] no traffic meta found", {
    lineId: lid,
    details,
    queryCandidates,
    lastFindArray,
  });

  return {};
}

function uniqueStrings(values: Array<unknown>) {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    if (value == null) continue;

    const str = String(value).trim();
    if (!str) continue;
    if (seen.has(str)) continue;

    seen.add(str);
    result.push(str);
  }

  return result;
}

function normalizeCompareValue(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/^linje/, "");
}

function getLineCompareValues(line: any) {
  return uniqueStrings([
    line?.id,
    line?.LineId,
    line?.LineID,
    line?.LineNrReal,
    line?.LineName,
    line?.PublicLineNumber,
    line?.LineNo,
    line?.LineNumber,
    line?.Designation,
    line?.Name,
    line?.name,
  ]).map(normalizeCompareValue);
}

function findMatchingTrafficRecord(
  findArr: any[],
  details: any,
  requestedLineId: string,
  query: string
) {
  if (!Array.isArray(findArr) || findArr.length === 0) return null;

  const wanted = new Set([
    ...getLineCompareValues(details),
    normalizeCompareValue(requestedLineId),
    normalizeCompareValue(query),
  ]);

  const direct = findArr.find((rec: any) => {
    const candidates = getLineCompareValues(rec);

    return candidates.some((candidate) => wanted.has(candidate));
  });

  if (direct) return direct;

  const lineNrReal = normalizeCompareValue(details?.LineNrReal);

  if (lineNrReal) {
    const byLineNrReal = findArr.find(
      (rec: any) => normalizeCompareValue(rec?.LineNrReal) === lineNrReal
    );

    if (byLineNrReal) return byLineNrReal;
  }

  return null;
}

function pickTrafficMeta(source: any): { iconCss?: string; groupCss?: string } {
  if (!source) return {};

  const iconCss =
    source?.Ogt?.OgtTrafficTypeIconCssClass ??
    source?.LineType?.OgtTrafficTypeIconCssClass ??
    source?.OgtTrafficTypeIconCssClass ??
    findFirstStringByKeyRegex(source, /TrafficType.*Icon.*CssClass/i, 5);

  const groupCss =
    source?.Ogt?.OgtTrafficTypeGroupCssClass ??
    source?.LineType?.OgtTrafficTypeGroupCssClass ??
    source?.OgtTrafficTypeGroupCssClass ??
    findFirstStringByKeyRegex(source, /TrafficType.*Group.*CssClass/i, 5);

  return { iconCss, groupCss };
}

function findFirstStringByKeyRegex(
  obj: unknown,
  keyRegex: RegExp,
  maxDepth = 4
): string | undefined {
  if (!obj || typeof obj !== "object" || maxDepth < 0) return undefined;

  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (keyRegex.test(key) && typeof value === "string") {
      return value;
    }
  }

  for (const value of Object.values(obj as Record<string, unknown>)) {
    const found = findFirstStringByKeyRegex(value, keyRegex, maxDepth - 1);

    if (found) {
      return found;
    }
  }

  return undefined;
}

//  HELPERS

// Pick active period ID from line details
function pickActivePeriodId(details: any): number | null {
  const periods = details?.LinePeriods
    ? (Object.values(details.LinePeriods) as any[])
    : [];
  if (!periods.length) return null;
  const now = new Date();
  const active = periods.find(
    (p) => new Date(p.FromDate) <= now && now <= new Date(p.ToDate)
  );
  if (active)
    return Number(active.TimeTablePeriodId ?? active.DateIntervalId ?? null);
  periods.sort((a, b) => +new Date(b.FromDate) - +new Date(a.FromDate));
  return Number(
    periods[0].TimeTablePeriodId ?? periods[0].DateIntervalId ?? null
  );
}

// Fetch parent stops by their IDs using WFS
async function fetchParentsByIds(hplnrs: string[]): Promise<Stop[]> {
  const typeName = await getFeatureTypeName("Stops");

  if (!hplnrs.length) return [];
  const props = ["hplnr", "hplnam", "geom"];
  const propertyXml = props
    .map((p) => `<wfs:PropertyName>${p}</wfs:PropertyName>`)
    .join("");
  const orXml = hplnrs
    .map(
      (id) => `
    <ogc:PropertyIsEqualTo>
      <ogc:PropertyName>hplnr</ogc:PropertyName>
      <ogc:Literal>${id}</ogc:Literal>
    </ogc:PropertyIsEqualTo>`
    )
    .join("");

  const xml = `
  <GetFeature service="WFS" version="1.0.0" outputFormat="application/json"
    xmlns:wfs="http://www.opengis.net/wfs"
    xmlns:ogc="http://www.opengis.net/ogc">
    <wfs:Query typeName="${typeName}">
      ${propertyXml}
      <ogc:Filter><ogc:Or>${orXml}</ogc:Or></ogc:Filter>
    </wfs:Query>
  </GetFeature>`.trim();

  const res = await fetch(API_ENDPOINTS.GEO_FETCH, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
    },
    body: `=${encodeURIComponent(xml)}`,
  });
  if (!res.ok) {
    console.error(
      "[hpl3 lookup] geo error:",
      res.status,
      await res.text().catch(() => "")
    );
    return [];
  }
  const gj = await res.json();
  const feats: any[] = gj?.features ?? [];
  return feats.map((f) => {
    const [lng, lat] = f.geometry?.coordinates ?? [0, 0];
    const p = f.properties ?? {};
    return {
      id: String(p.hplnr),
      name: String(p.hplnam ?? "").trim(),
      lat,
      lng,
    } as Stop;
  });
}

// Fetch parent stops for a line via the "find" endpoint
export async function fetchParentStopsViaFind(lineId: string): Promise<Stop[]> {
  const lid = String(lineId);

  const detRes = await fetch(API_ENDPOINTS.LINES_DETAILS(lid));
  if (!detRes.ok) {
    console.error(
      "[parentViaFind] details error:",
      detRes.status,
      await detRes.text().catch(() => "")
    );
    return [];
  }

  const detJson = await detRes.json();
  const details = Array.isArray(detJson) ? detJson[0] : detJson;

  let periodId = await getActiveTpid();
  if (periodId == null) periodId = pickActivePeriodId(details);

  if (!periodId) {
    console.warn("[parentViaFind] no active period for line", lid);
    return [];
  }

  const findUrl = API_ENDPOINTS.LINES_FIND(
    details.LineName ?? lid,
    String(periodId),
    true
  );

  const findRes = await fetch(findUrl);
  if (!findRes.ok) {
    console.error(
      "[parentViaFind] find error:",
      findRes.status,
      await findRes.text().catch(() => "")
    );
    return [];
  }

  const findArr = await findRes.json();
  const rec = Array.isArray(findArr)
    ? findArr.find(
        (x: any) => String(x.LineNrReal) === String(details.LineNrReal)
      )
    : null;

  const passing: number[] = rec?.Ogt?.PassingStops ?? [];
  const parentIds = Array.from(new Set(passing.map((x) => String(x))));
  if (!parentIds.length) return [];

  return fetchParentsByIds(parentIds);
}
